import React from 'react';
import { Result, Button, Spin } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import * as Sentry from '@sentry/react';

/**
 * Error boundary to catch component errors and display graceful fallback UI
 * Logs errors to Sentry and provides recovery options
 */
export class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            errorId: null,
            errorMessage: '',
            loading: false,
        };
    }

    static getDerivedStateFromError(error) {
        return {
            hasError: true,
            errorMessage: error.message || 'An unexpected error occurred',
        };
    }

    componentDidCatch(error, errorInfo) {
        // Capture exception with Sentry
        const eventId = Sentry.captureException(error, {
            contexts: {
                react: {
                    componentStack: errorInfo.componentStack,
                },
            },
        });
        this.setState({ errorId: eventId });
        console.error('Error caught by boundary:', error, errorInfo);
    }

    handleReset = () => {
        this.setState({ hasError: false, errorId: null, errorMessage: '' });
    };

    handleReload = () => {
        this.setState({ loading: true });
        window.location.reload();
    };

    render() {
        if (this.state.hasError) {
            return (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', padding: '24px' }}>
                    <Spin spinning={this.state.loading}>
                        <Result
                            status="error"
                            title="Oops! Something went wrong"
                            subTitle={this.state.errorMessage || 'An unexpected error occurred. Our team has been notified.'}
                            extra={[
                                <Button
                                    key="reset"
                                    onClick={this.handleReset}
                                    style={{ marginRight: '8px' }}
                                >
                                    Try Again
                                </Button>,
                                <Button
                                    key="reload"
                                    icon={<ReloadOutlined />}
                                    onClick={this.handleReload}
                                    loading={this.state.loading}
                                >
                                    Reload Page
                                </Button>,
                            ]}
                        />
                        {this.state.errorId && (
                            <div style={{ marginTop: '24px', textAlign: 'center', fontSize: '12px', color: '#999' }}>
                                Error ID: {this.state.errorId}
                            </div>
                        )}
                    </Spin>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
